import classNames from 'classnames';
import { useCallback } from 'react';

import { Radio, RadioGroup, Text } from '@revelio/ui';

import { PreferenceEditorSectionHeading } from '../components/PreferenceEditorSectionHeading';
import {
  ECONOMIC_IMPACT_INDUSTRY_CLASSIFICATION_LABEL,
  type EconomicImpactIndustryClassification,
  isEconomicImpactIndustryClassification,
  PreferenceScope,
  resolveEconomicImpactIndustryClassification,
  UserPreferenceKey,
} from '../types';
import { PreferenceBrowserStorage } from '../utils/preference-session-storage';
import layoutStyles from './economic-impact-editor.module.css';
import styles from './preference-editors.module.css';
import type {
  PreferenceEditorProps,
  PreferenceMeta,
} from './preferenceEditors';

// Beside the page's Geography in the rail — the only modal that shows either.
export const meta: PreferenceMeta = {
  id: UserPreferenceKey.EconomicImpactIndustries,
  label: 'Industries',
};

/**
 * Which industry list the Economic Impact scenario form offers. Only the
 * economic-impact page's own preferences button surfaces this editor — the
 * CustomDash and home buttons hide the key — but the choice is a single GLOBAL
 * record like the other taxonomy-style preferences.
 */
export default function EconomicImpactIndustriesEditor({
  onPreferenceUpdate,
  preferences,
  diff,
}: PreferenceEditorProps) {
  // Derived from props rather than mirrored in state: the modal's provider
  // already re-renders the editor on every store change.
  const classification = resolveEconomicImpactIndustryClassification(
    preferences[UserPreferenceKey.EconomicImpactIndustries]
  );

  const onUpdate = useCallback(
    (value: EconomicImpactIndustryClassification) => {
      // Single GLOBAL choice: always write the GLOBAL record regardless of
      // which page's modal made the change (same pattern as DataAdjustments).
      PreferenceBrowserStorage.update(
        PreferenceScope.GLOBAL,
        UserPreferenceKey.EconomicImpactIndustries,
        value
      );
      onPreferenceUpdate?.(UserPreferenceKey.EconomicImpactIndustries, value);
    },
    [onPreferenceUpdate]
  );

  return (
    <>
      <PreferenceEditorSectionHeading
        title="Industry classification"
        indicateDiff={diff?.includes(meta.id) ?? false}
        tooltip="The industry list you pick from when adding an investment."
      />
      <RadioGroup
        value={classification}
        onChange={(value) => {
          if (isEconomicImpactIndustryClassification(value)) onUpdate(value);
        }}
        className={styles.radioGroup}
      >
        <div className={layoutStyles.choiceList}>
          <div className={layoutStyles.choice}>
            <Radio
              value="rics"
              className={styles.appearanceRadio}
              data-testid="economic-impact-industries-rics"
            >
              {ECONOMIC_IMPACT_INDUSTRY_CLASSIFICATION_LABEL.rics}
            </Radio>
            <Text
              fontSize="x-sm"
              className={classNames(
                styles.description,
                styles.descriptionIndent
              )}
            >
              Revelio's own industry taxonomy, as used across the rest of the
              dashboard.
            </Text>
          </div>
          <div className={layoutStyles.choice}>
            <Radio
              value="bea"
              className={styles.appearanceRadio}
              data-testid="economic-impact-industries-bea"
            >
              {ECONOMIC_IMPACT_INDUSTRY_CLASSIFICATION_LABEL.bea}
            </Radio>
            <Text
              fontSize="x-sm"
              className={classNames(
                styles.description,
                styles.descriptionIndent
              )}
            >
              The 396 industries defined by the U.S. Bureau of Economic
              Analysis, searchable by BEA or NAICS code.
            </Text>
          </div>
        </div>
      </RadioGroup>
    </>
  );
}
